import { 
  collection,
  getDocs,
  getDoc,
  query,
  where,
  doc,
  setDoc,
  serverTimestamp
} from 'firebase/firestore';
import { db, collections } from '@/lib/firebase';

export interface Automation {
  id: string;
  description: string;
  url: string;
  environment: 'production' | 'staging';
  created_at: any;
  updated_at: any;
}

// Initialize default automations
const defaultAutomations: Omit<Automation, 'created_at' | 'updated_at'>[] = [
  {
    id: 'new-user',
    description: 'Webhook for new user registrations',
    url: 'https://hook.us2.make.com/2x4598hee5ccqlvk4adhgfg51xbtqidn',
    environment: 'production'
  }
];

export async function initializeAutomations(): Promise<void> {
  try {
    const automationsRef = collection(db, collections.automations);
    const snapshot = await getDocs(automationsRef);

    // Only initialize if collection is empty
    if (snapshot.empty) {
      for (const automation of defaultAutomations) {
        const docRef = doc(automationsRef, automation.id);
        await setDoc(docRef, {
          ...automation,
          created_at: serverTimestamp(),
          updated_at: serverTimestamp()
        });
      }
    }
  } catch (error) {
    console.error('Error initializing automations:', error);
    throw new Error('Failed to initialize automations');
  }
}

export async function getAutomationByEnvironment(environment: string): Promise<Automation[]> {
  try {
    const automationsRef = collection(db, collections.automations);
    const q = query(automationsRef, where('environment', '==', environment));
    const snapshot = await getDocs(q);

    return snapshot.docs.map(doc => ({
      ...doc.data(),
      id: doc.id
    })) as Automation[];
  } catch (error) {
    console.error('Error fetching automations:', error);
    throw new Error('Failed to fetch automations');
  }
}

export async function getAutomationById(id: string): Promise<Automation | null> {
  try {
    const docRef = doc(db, collections.automations, id);
    const docSnapshot = await getDoc(docRef);

    if (!docSnapshot.exists()) {
      // Return default automation if exists
      const defaultAutomation = defaultAutomations.find(a => a.id === id);
      if (defaultAutomation) {
        console.info(`Using default automation for ${id}`);
        return defaultAutomation;
      }
      console.warn(`No automation found for ${id}`);
      return null;
    }

    const data = docSnapshot.data();
    return {
      ...data,
      id: docSnapshot.id
    } as Automation;
  } catch (error) {
    // Return default automation if exists
    const defaultAutomation = defaultAutomations.find(a => a.id === id);
    if (defaultAutomation) {
      console.info(`Using default automation for ${id} due to error:`, error);
      return defaultAutomation;
    }
    console.error('Error fetching automation:', error);
    throw error;
  }
}